import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useEdgesState,
  useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Material UI Icons & Components
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import TuneIcon from "@mui/icons-material/Tune";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";

// APIs & Contexts
import { getData, postData, put } from "../../apis/api";
import { ENDPOINTS } from "../../apis/urls";
import { useTheme } from "../../contexts/ThemeContext";

// Constants & HOC
import { generateId, initialNodes, NodeTypes } from "./constants";
import withFunctions from "./nodes/willFunctions";
import { PopperModal } from "./popperModal";

// Multi-Channel Journey Node Components
import { StartNode } from "./nodes/startNode";
import { OutboundCallNode, OutboundCallEditModal } from "./nodes/outboundCall";
import { SendSmsNode, SendSmsEditModal } from "./nodes/sendSms";
import { SendEmailNode, SendEmailEditModal } from "./nodes/sendEmail";
import { HttpRequestNode, HttpRequestEditModal } from "./nodes/httpRequest";
import { EndNode, EndNodeEditModal } from "./nodes/endNode";
import { DelayNode, DelayNodeEditModal } from "./nodes/delay";
import { ConditionalNode, ConditionalNodeContent } from "./nodes/conditional";
import { GenericNode, GenericNodeEditModal, GENERIC_NODE_TYPE } from "./nodes/genericNode";

// Legacy WhatsApp Node Components
import { TextNode, TextNodeEditModal } from "./nodes/msgNode";
import { SendMessageNode, SendMessageEditModal } from "./nodes/sendMessage";
import { ListNode, ListEditModal } from "./nodes/list";
import { QuickReplyNode, QuickReplyEditModal } from "./nodes/quickReply";
import { ImageNode, ImageNodeModal } from "./nodes/image";
import { DocumentNode, DocumentNodeModal } from "./nodes/document";

// Modals for Triggers & Settings
import { TriggerModal } from "./triggerModal";
import { SettingsModal } from "./settingsModal";
import InsightsModal from "./insightsModal";
import "./workflowV2.css";

const BACKEND_SUPPORTED_NODE_TYPES = new Set([
  NodeTypes.Start,
  NodeTypes.OutboundCall,
  NodeTypes.SendSMS,
  NodeTypes.Delay,
  NodeTypes.SendEmail,
  NodeTypes.HttpRequest,
  NodeTypes.Conditional,
  NodeTypes.End,
  // AI
  NodeTypes.AiClassify,
  // Instagram
  NodeTypes.SendInstagramMessage,
  NodeTypes.SendInstagramComment,
  // CRM & Data
  NodeTypes.SyncLeadSquared,
  NodeTypes.UpdateContact,
  NodeTypes.TagContact,
  NodeTypes.CreateTask,
  // Journey Control
  NodeTypes.WaitForEvent,
  // Legacy
  NodeTypes.Text,
  NodeTypes.SendMessage,
  NodeTypes.List,
]);

// Node types with a dedicated canvas component. Everything else the backend
// sends (Instagram, AI, CRM, wait, lead-owner WhatsApp, ...) is drawn and
// edited by GenericNode from the backend's fieldsSchema, so the editor always
// writes the config keys the executor reads.
const CUSTOM_RENDERED_NODE_TYPES = new Set([
  NodeTypes.Start,
  NodeTypes.OutboundCall,
  NodeTypes.SendSMS,
  NodeTypes.Delay,
  NodeTypes.SendEmail,
  NodeTypes.HttpRequest,
  NodeTypes.Conditional,
  NodeTypes.End,
]);

// WorkflowTrigger.TRIGGER_TYPES labels shown on the START node
const TRIGGER_LABELS = {
  INBOUND_CALL_COMPLETED: "Inbound Call",
  OUTBOUND_CALL_COMPLETED: "Outbound Call",
  CONTACT_CREATED: "Contact Created",
  TAG_ADDED: "Tag Added",
  INBOUND_MESSAGE_RECEIVED: "Inbound Message",
  INSTAGRAM_COMMENT_RECEIVED: "Instagram Comment",
  INSTAGRAM_DM_RECEIVED: "Instagram DM",
  INSTAGRAM_STORY_MENTION: "Instagram Story Mention",
  LEADSQUARED_OWNER_CHANGED: "LeadSquared Owner Changed",
  MANUAL: "Manual",
  WEBHOOK: "Webhook",
};

const WorkflowV2 = () => {
  const { id } = useParams();
  const history = useHistory();
  const { darkMode } = useTheme();

  // Workflow Record Metadata
  const [workflow, setWorkflow] = useState(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [workFlowName, setWorkFlowName] = useState("New Journey");
  const [editName, setEditName] = useState(false);
  const [tempName, setTempName] = useState("");
  const [workflowStatus, setWorkflowStatus] = useState("draft");
  const [workflowVersion, setWorkflowVersion] = useState(1);

  // ReactFlow Canvas State
  const reactFlowWrapper = useRef(null);
  const nodeFunctionsRef = useRef({});
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Status & Loaders
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Global Dialog Modals
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    valid: false,
    errors: [],
  });

  // Popper for Adding Nodes
  const [anchorEl, setAnchorEl] = useState(null);
  const [popperOpen, setPopperOpen] = useState({
    isOpen: false,
    prevId: null,
    extraData: null,
  });

  // Node Edit Modals State
  const [activeEditModal, setActiveEditModal] = useState({
    type: null,
    id: null,
    data: null,
    open: false,
  });

  // Load workflow from backend
  const fetchWorkflow = useCallback(async () => {
    if (!id) {
      setEditName(true);
      return;
    }
    setLoading(true);
    try {
      const res = await getData(ENDPOINTS.WORKFLOW.WORKFLOW(id));
      const wf = res?.Content || res;
      if (wf && wf.id) {
        setWorkflow(wf);
        setWorkFlowName(wf.name || "Untitled Journey");
        setTempName(wf.name || "Untitled Journey");
        setWorkflowStatus(wf.status || "draft");
        setWorkflowVersion(wf.version || 1);

        const loadedNodes = wf.graph?.nodes || [];
        const loadedEdges = wf.graph?.edges || [];

        if (loadedNodes.length > 0) {
          const triggerSummary = (wf.triggers || [])
            .filter((t) => t.enabled)
            .map((t) => TRIGGER_LABELS[t.triggerType] || t.triggerType)
            .join(", ");

          // Normalize nodes: ensure nodeType in data matches node.type
          const normalizedNodes = loadedNodes.map((n) => {
            const nType = (n.data?.nodeType || n.type || "START").toUpperCase();
            return {
              ...n,
              // data.nodeType keeps the real type, so saves still send it
              type: CUSTOM_RENDERED_NODE_TYPES.has(nType) ? nType : GENERIC_NODE_TYPE,
              data: {
                ...(n.data || {}),
                nodeType: nType,
                ...(nType === NodeTypes.Start ? { triggerSummary } : {}),
              },
            };
          });
          setNodes(normalizedNodes);
          setEdges(loadedEdges);
        } else {
          setNodes(initialNodes);
          setEdges([]);
        }
      }
    } catch (err) {
      console.error("Failed to load workflow:", err);
      toast.error("Could not load workflow details");
    } finally {
      setLoading(false);
    }
  }, [id, setEdges, setNodes]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  // Dynamic layout positioning for newly added connected nodes
  const getNextNodePosition = useCallback(
    (sourceId, extraData) => {
      const sourceNode = nodes.find((n) => n.id === sourceId) || nodes[nodes.length - 1];
      const basePosition = sourceNode?.position || { x: 250, y: 150 };
      const branchOffset =
        extraData?.branchIndex !== undefined ? (extraData.branchIndex - 1) * 140 : 0;

      return {
        x: basePosition.x + 300,
        y: basePosition.y + branchOffset,
      };
    },
    [nodes]
  );

  // Add edge and connect
  const getEdgeForNode = useCallback((prevId, targetId, extraData) => {
    const sourceId = prevId || "1";
    const sourceHandle = extraData?.key || extraData?.branchKey || "next";
    return {
      id: `e-${sourceId}-${sourceHandle}-${targetId}`,
      source: sourceId,
      target: targetId,
      sourceHandle: sourceHandle,
    };
  }, []);

  // Add node via (+) on handle
  const onAdd = useCallback((e, rest, nodeId = null, extraData = null) => {
    setAnchorEl(e?.currentTarget);
    setPopperOpen({
      isOpen: true,
      prevId: rest?.id || nodeId,
      extraData,
    });
  }, []);

  // Handler when user selects a node from PopperModal
  // `meta` is the backend catalogue entry for node types drawn by GenericNode.
  const handleNodeSelect = useCallback(
    (nodeType, meta = null) => {
      const parentId = popperOpen.prevId;
      const extraData = popperOpen.extraData;
      setPopperOpen({ isOpen: false, prevId: null, extraData: null });

      const newId = generateId();
      const position = getNextNodePosition(parentId, extraData);

      const isGeneric = !!meta && !CUSTOM_RENDERED_NODE_TYPES.has(nodeType);
      const newNode = {
        id: newId,
        type: isGeneric ? GENERIC_NODE_TYPE : nodeType,
        position,
        data: isGeneric
          ? {
              nodeType,
              label: meta.displayName || nodeType.replace(/_/g, " "),
              displayName: meta.displayName,
              icon: meta.icon,
              color: meta.color,
              badge: meta.badge,
              description: meta.description,
              fieldsSchema: meta.fieldsSchema || [],
              supportedTransitions: meta.supportedTransitions || [],
              config: { ...(meta.defaultConfig || {}) },
            }
          : {
              nodeType,
              label: nodeType.replace(/_/g, " "),
              config: {},
            },
      };

      setNodes((current) => current.concat(newNode));

      if (parentId) {
        const edge = getEdgeForNode(parentId, newId, extraData);
        setEdges((currentEdges) => addEdge(edge, currentEdges));
      }

      // Automatically open configuration modal for the newly added node
      setActiveEditModal({
        type: nodeType,
        id: newId,
        data: newNode,
        open: true,
      });
    },
    [getEdgeForNode, getNextNodePosition, popperOpen, setEdges, setNodes]
  );

  // Open Edit Modal for a node
  const onEdit = useCallback((nodeProps) => {
    const nodeType = (nodeProps.data?.nodeType || nodeProps.type || "").toUpperCase();
    setActiveEditModal({
      type: nodeType,
      id: nodeProps.id,
      data: nodeProps,
      open: true,
    });
  }, []);

  // Save node configuration from modal back into node data
  const handleModalClose = useCallback(
    (updatedData, isEdit) => {
      const activeId = activeEditModal.id;
      if (updatedData && activeId) {
        setNodes((current) =>
          current.map((n) => {
            if (n.id !== activeId) return n;
            return {
              ...n,
              data: {
                ...n.data,
                // The backend summary describes the old config; GenericNode falls back to the new one.
                ...(n.type === GENERIC_NODE_TYPE ? { summary: null } : {}),
                ...updatedData,
                config: {
                  ...(n.data?.config || {}),
                  ...(updatedData.config || {}),
                },
              },
            };
          })
        );
      }
      setActiveEditModal({ type: null, id: null, data: null, open: false });
    },
    [activeEditModal.id, setNodes]
  );

  // Delete node
  const onDeleteNode = useCallback(
    (nodeId) => {
      if (nodeId === "1") {
        toast.error("The root Journey trigger cannot be deleted");
        return;
      }
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      toast.success("Step removed");
    },
    [setEdges, setNodes]
  );

  // Trigger modal handler from START node
  const onTrigger = useCallback(() => {
    setTriggerModalOpen(true);
  }, []);

  // Functions injected into node components via withFunctions HOC
  const nodeFunctions = useMemo(
    () => ({
      onEdit,
      onAdd,
      onDeleteNode,
      onTrigger,
      edges,
    }),
    [edges, onAdd, onDeleteNode, onEdit, onTrigger]
  );

  nodeFunctionsRef.current = nodeFunctions;

  // React Flow Node Registry
  const nodeTypes = useMemo(
    () => ({
      [NodeTypes.Start]: withFunctions(StartNode, nodeFunctionsRef),
      START: withFunctions(StartNode, nodeFunctionsRef),
      start: withFunctions(StartNode, nodeFunctionsRef),

      [NodeTypes.OutboundCall]: withFunctions(OutboundCallNode, nodeFunctionsRef),
      OUTBOUND_CALL: withFunctions(OutboundCallNode, nodeFunctionsRef),

      [NodeTypes.SendSMS]: withFunctions(SendSmsNode, nodeFunctionsRef),
      SEND_SMS: withFunctions(SendSmsNode, nodeFunctionsRef),

      [NodeTypes.Delay]: withFunctions(DelayNode, nodeFunctionsRef),
      DELAY: withFunctions(DelayNode, nodeFunctionsRef),
      delay: withFunctions(DelayNode, nodeFunctionsRef),

      [NodeTypes.SendEmail]: withFunctions(SendEmailNode, nodeFunctionsRef),
      SEND_EMAIL: withFunctions(SendEmailNode, nodeFunctionsRef),

      [NodeTypes.HttpRequest]: withFunctions(HttpRequestNode, nodeFunctionsRef),
      HTTP_REQUEST: withFunctions(HttpRequestNode, nodeFunctionsRef),

      [NodeTypes.Conditional]: withFunctions(ConditionalNode, nodeFunctionsRef),
      CONDITION: withFunctions(ConditionalNode, nodeFunctionsRef),
      condition: withFunctions(ConditionalNode, nodeFunctionsRef),

      [NodeTypes.End]: withFunctions(EndNode, nodeFunctionsRef),
      END: withFunctions(EndNode, nodeFunctionsRef),

      [GENERIC_NODE_TYPE]: withFunctions(GenericNode, nodeFunctionsRef),

      // Legacy fallback
      [NodeTypes.Text]: withFunctions(TextNode, nodeFunctionsRef),
      [NodeTypes.SendMessage]: withFunctions(SendMessageNode, nodeFunctionsRef),
      [NodeTypes.List]: withFunctions(ListNode, nodeFunctionsRef),
      [NodeTypes.QuickReply]: withFunctions(QuickReplyNode, nodeFunctionsRef),
      [NodeTypes.Image]: withFunctions(ImageNode, nodeFunctionsRef),
      [NodeTypes.Document]: withFunctions(DocumentNode, nodeFunctionsRef),
    }),
    []
  );

  // Connect edge
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const hasSame = eds.some(
          (e) =>
            e.source === params.source &&
            e.sourceHandle === params.sourceHandle &&
            e.target === params.target
        );
        if (hasSame || params.source === params.target) return eds;
        return addEdge(params, eds);
      });
    },
    [setEdges]
  );

  // Normalize graph payload matching backend compile_graph_to_nodes
  const buildGraphPayload = useCallback(() => {
    const cleanNodes = nodes.map((n) => ({
      id: String(n.id),
      type: (n.data?.nodeType || n.type || "START").toUpperCase(),
      position: n.position || { x: 0, y: 0 },
      data: {
        nodeType: (n.data?.nodeType || n.type || "START").toUpperCase(),
        label: n.data?.label || "",
        config: n.data?.config || {},
        transitions: n.data?.transitions || {},
      },
    }));

    const cleanEdges = edges.map((e) => ({
      id: e.id,
      source: String(e.source),
      target: String(e.target),
      sourceHandle: e.sourceHandle || "next",
    }));

    return {
      nodes: cleanNodes,
      edges: cleanEdges,
    };
  }, [edges, nodes]);

  // Save workflow (Draft)
  const saveWorkflow = async (showToast = true) => {
    const trimmed = workFlowName.trim();
    if (!trimmed) {
      toast.error("Please provide a Journey name");
      setEditName(true);
      return null;
    }

    const graph = buildGraphPayload();
    const payload = {
      name: trimmed,
      description: workflow?.description || "",
      campaignName: workflow?.campaignName || null,
      timezone: workflow?.timezone || "Asia/Kolkata",
      reEnrollPolicy: workflow?.reEnrollPolicy || "skip",
      maxActivePerContact: workflow?.maxActivePerContact ?? 1,
      respectQuietHours: workflow?.respectQuietHours ?? true,
      quietHoursStart: workflow?.quietHoursStart || null,
      quietHoursEnd: workflow?.quietHoursEnd || null,
      graph,
    };

    setSaving(true);
    try {
      if (id) {
        const res = await put(ENDPOINTS.WORKFLOW.WORKFLOW(id), payload);
        if (showToast) toast.success("Journey draft saved!");
        const updated = res?.Content || res;
        setWorkflow(updated);
        return updated;
      } else {
        const res = await postData(ENDPOINTS.WORKFLOW.WORKFLOWS, payload);
        const created = res?.Content || res;
        toast.success("Journey created!");
        if (created?.id) {
          history.push(`/workflow-editor/${created.id}`);
        }
        return created;
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err?.message || "Failed to save workflow");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Validate graph
  const validateWorkflow = async () => {
    if (!id) {
      toast.info("Please save draft before validating");
      await saveWorkflow(false);
      return;
    }

    const graph = buildGraphPayload();
    try {
      const res = await postData(ENDPOINTS.WORKFLOW.VALIDATE(id), { graph });
      if (res.valid) {
        toast.success("Graph is valid and ready to publish!");
        setValidationDialog({ open: true, valid: true, errors: [] });
      } else {
        setValidationDialog({
          open: true,
          valid: false,
          errors: res.errors || ["Validation failed"],
        });
      }
    } catch (err) {
      toast.error("Validation error: " + (err?.message || "Network error"));
    }
  };

  // Publish workflow version
  const publishWorkflow = async () => {
    if (!id) {
      const saved = await saveWorkflow(false);
      if (!saved?.id) return;
    }

    // Auto-save graph before publishing
    await saveWorkflow(false);

    setPublishing(true);
    try {
      const graph = buildGraphPayload();
      const res = await postData(ENDPOINTS.WORKFLOW.PUBLISH(id), { graph });
      toast.success(res.message || `Journey published as v${res.version}!`);
      setWorkflowStatus("active");
      if (res.version) setWorkflowVersion(res.version);
      fetchWorkflow();
    } catch (err) {
      console.error("Publish error:", err);
      const errors = err?.errors || [err?.message || "Publish rejected by validator"];
      setValidationDialog({
        open: true,
        valid: false,
        errors,
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className={`workflow-editor-shell ${darkMode ? "dark" : ""}`}>
      <div className="workflow-editor-main">
        {/* Top App Bar */}
        <div
          className={`workflow-editor-toolbar ${
            darkMode ? "bg-[#121212] border-b border-zinc-800" : "bg-white"
          }`}
        >
          {/* Left: Back & Name */}
          <div className="workflow-editor-titleGroup">
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/workflow")}
              style={{
                color: darkMode ? "#a1a1aa" : "#475569",
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Journeys
            </Button>

            <div className="flex items-center gap-2">
              {editName ? (
                <div className="flex items-center gap-1">
                  <TextField
                    size="small"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    placeholder="Enter Journey Name"
                    autoFocus
                    inputProps={{
                      style: {
                        fontSize: 16,
                        fontWeight: 700,
                        padding: "4px 8px",
                        color: darkMode ? "#fff" : "#0f172a",
                      },
                    }}
                  />
                  <DoneIcon
                    className="cursor-pointer text-emerald-500 hover:text-emerald-600"
                    fontSize="small"
                    onClick={() => {
                      if (tempName.trim()) setWorkFlowName(tempName.trim());
                      setEditName(false);
                    }}
                  />
                  <CloseIcon
                    className="cursor-pointer text-slate-400 hover:text-slate-600"
                    fontSize="small"
                    onClick={() => setEditName(false)}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1
                    className={`text-lg font-bold tracking-tight cursor-pointer ${
                      darkMode ? "text-white" : "text-slate-900"
                    }`}
                    onClick={() => {
                      setTempName(workFlowName);
                      setEditName(true);
                    }}
                  >
                    {workFlowName}
                  </h1>
                  <EditIcon
                    className="cursor-pointer text-slate-400 hover:text-indigo-500"
                    style={{ fontSize: 15 }}
                    onClick={() => {
                      setTempName(workFlowName);
                      setEditName(true);
                    }}
                  />
                </div>
              )}

              {/* Status & Version Badges */}
              <Chip
                label={workflowStatus.toUpperCase()}
                size="small"
                style={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 700,
                  backgroundColor:
                    workflowStatus === "active"
                      ? "#10b98122"
                      : workflowStatus === "paused"
                      ? "#f59e0b22"
                      : "#71717a22",
                  color:
                    workflowStatus === "active"
                      ? "#10b981"
                      : workflowStatus === "paused"
                      ? "#f59e0b"
                      : "#71717a",
                }}
              />
              <Chip
                label={`v${workflowVersion}`}
                size="small"
                variant="outlined"
                style={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 600,
                  borderColor: darkMode ? "#3f3f46" : "#cbd5e1",
                  color: darkMode ? "#a1a1aa" : "#64748b",
                }}
              />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="workflow-editor-actions">
            <Button variant="outlined" size="small" disabled={!workflow?.id} onClick={() => setInsightsOpen(true)}>
              Contacts & metrics
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FlashOnIcon />}
              onClick={() => setTriggerModalOpen(true)}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                borderColor: darkMode ? "#3f3f46" : "#cbd5e1",
                color: darkMode ? "#e4e4e7" : "#334155",
              }}
            >
              Triggers
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon />}
              onClick={() => setSettingsModalOpen(true)}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                borderColor: darkMode ? "#3f3f46" : "#cbd5e1",
                color: darkMode ? "#e4e4e7" : "#334155",
              }}
            >
              Settings
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={validateWorkflow}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                borderColor: darkMode ? "#3f3f46" : "#cbd5e1",
                color: darkMode ? "#e4e4e7" : "#334155",
              }}
            >
              Validate
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<SaveIcon />}
              onClick={() => saveWorkflow(true)}
              disabled={saving}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                borderColor: "#6366f1",
                color: "#6366f1",
              }}
            >
              {saving ? "Saving..." : "Save Draft"}
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={publishWorkflow}
              disabled={publishing}
              style={{
                backgroundColor: "#2563eb",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12,
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
              }}
            >
              {publishing ? "Publishing..." : "Publish Journey"}
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={reactFlowWrapper}
          className="workflow-editor-canvasWrap"
          style={{ width: "100%", height: "calc(100vh - 84px)" }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onInit={setReactFlowInstance}
            fitView
            minZoom={0.2}
            maxZoom={2}
          >
            <Background
              color={darkMode ? "#27272a" : "#cbd5e1"}
              gap={20}
              size={1}
            />
            <Controls
              style={{
                backgroundColor: darkMode ? "#18181b" : "#ffffff",
                border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
                borderRadius: 8,
              }}
            />
            <MiniMap
              nodeStrokeColor={darkMode ? "#3f3f46" : "#cbd5e1"}
              nodeColor={darkMode ? "#27272a" : "#f1f5f9"}
              maskColor={darkMode ? "rgba(0, 0, 0, 0.6)" : "rgba(240, 242, 245, 0.7)"}
              style={{
                backgroundColor: darkMode ? "#18181b" : "#ffffff",
                border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
                borderRadius: 8,
              }}
            />
          </ReactFlow>
        </div>
      </div>

      {/* Popper for adding nodes */}
      <PopperModal
        open={popperOpen.isOpen}
        anchorEl={anchorEl}
        onNodeAdded={handleNodeSelect}
      />

      {/* Node Config Modals */}
      <OutboundCallEditModal
        open={activeEditModal.type === "OUTBOUND_CALL"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <SendSmsEditModal
        open={activeEditModal.type === "SEND_SMS"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <SendEmailEditModal
        open={activeEditModal.type === "SEND_EMAIL"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <HttpRequestEditModal
        open={activeEditModal.type === "HTTP_REQUEST"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <DelayNodeEditModal
        open={activeEditModal.type === "DELAY"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <ConditionalNodeContent
        open={activeEditModal.type === "CONDITION"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <EndNodeEditModal
        open={activeEditModal.type === "END"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <GenericNodeEditModal
        open={
          activeEditModal.open &&
          activeEditModal.data?.type === GENERIC_NODE_TYPE &&
          !["TEXT", "SENDMESSAGE"].includes(activeEditModal.type)
        }
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />

      {/* Legacy WhatsApp Node Modals for backward compatibility */}
      <TextNodeEditModal
        open={activeEditModal.type === "TEXT"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />
      <SendMessageEditModal
        open={activeEditModal.type === "SENDMESSAGE"}
        data={activeEditModal.data}
        handleClose={handleModalClose}
      />

      {/* Trigger Configuration Modal */}
      <InsightsModal open={insightsOpen} onClose={() => setInsightsOpen(false)} workflowId={workflow?.id} darkMode={darkMode} />
      <TriggerModal
        open={triggerModalOpen}
        handleClose={() => setTriggerModalOpen(false)}
        workflowId={id}
        onUpdated={fetchWorkflow}
      />

      {/* Journey Settings Modal */}
      <SettingsModal
        open={settingsModalOpen}
        handleClose={() => setSettingsModalOpen(false)}
        workflow={workflow}
        onSave={async (settings) => {
          if (id) {
            try {
              const res = await put(ENDPOINTS.WORKFLOW.WORKFLOW(id), settings);
              setWorkflow(res?.Content || res);
              toast.success("Settings applied!");
            } catch (e) {
              toast.error("Failed to update settings");
            }
          }
        }}
      />

      {/* Validation Dialog */}
      <Dialog
        open={validationDialog.open}
        onClose={() => setValidationDialog({ open: false, valid: false, errors: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={darkMode ? "bg-zinc-900 text-white" : ""}>
          {validationDialog.valid ? "Workflow Validation Passed" : "Workflow Validation Issues"}
        </DialogTitle>
        <DialogContent className={darkMode ? "bg-zinc-900 text-white" : ""}>
          {validationDialog.valid ? (
            <Alert severity="success" className="my-2">
              All nodes, configurations, and transitions are valid and verified. Your Journey is ready to publish!
            </Alert>
          ) : (
            <div className="space-y-2 my-2">
              <Alert severity="error">
                Please resolve the following issues before publishing:
              </Alert>
              <ul className="list-disc pl-5 space-y-1 text-xs text-rose-500 font-medium">
                {validationDialog.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
        <DialogActions className={darkMode ? "bg-zinc-900" : ""}>
          <Button
            onClick={() => setValidationDialog({ open: false, valid: false, errors: [] })}
            variant="contained"
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WorkflowV2;
